import {
  Box,
  type BoxProps,
  type ElementProps,
  rem,
  rgba,
  useMantineColorScheme,
  useMantineTheme,
} from "@mantine/core";
import React, { forwardRef } from "react";

export const BADGE_VARIANTS = ["brand", "danger", "neutral", "active"] as const;
export type BadgeVariant = (typeof BADGE_VARIANTS)[number];

export type BadgeSize = "sm" | "md";

// Tamanhos do chip: "sm" (padrão/original) e "md" (um pouco maior, usado nos filtros).
const BADGE_SIZE_STYLES: Record<BadgeSize, { padding: string; fz: string }> = {
  sm: { padding: `${rem(2)} ${rem(6)}`, fz: "sm" },
  md: { padding: `${rem(4)} ${rem(10)}`, fz: "sm" },
};

interface BadgeProps extends BoxProps, ElementProps<"div"> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  /** Ícone/elemento opcional exibido antes do texto (ex.: funil do filtro de tags). */
  leftSection?: React.ReactNode;
  "data-filter"?: string;
}

// forwardRef + spread de props permitem usar o Badge como alvo de Popover
// (ex.: ChatTagFilter), recebendo ref/onClick/aria injetados pelo Mantine.
const Badge = forwardRef<HTMLDivElement, BadgeProps>(function Badge(
  { children, variant = "neutral", size = "sm", leftSection, style, ...others },
  ref
) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isLight = colorScheme === "light";

  const variantStyles: Record<
    BadgeVariant,
    { bg: string; color: string; fw: "bold" | "normal" }
  > = {
    brand: {
      bg: rgba(theme.colors["R500"][0], isLight ? 0.2 : 0.4),
      color: theme.colors["R500"][0],
      fw: "bold",
    },
    active: {
      bg: theme.colors["R500"][0],
      color: theme.colors["L100"][0],
      fw: "bold",
    },
    danger: {
      bg: rgba(theme.colors["V500"][0], isLight ? 0.2 : 0.3),
      color: theme.colors["V500"][0],
      fw: "bold",
    },
    neutral: {
      bg: rgba(theme.colors["L500"][0], isLight ? 0.3 : 0.2),
      color: rgba(theme.colors["plain_text"][0], isLight ? 0.6 : 1),
      fw: "normal",
    },
  };

  const { bg, color, fw } = variantStyles[variant];

  return (
    <Box
      ref={ref}
      bg={bg}
      fw={fw}
      style={{
        borderRadius: rem(4),
        cursor: "pointer",
        // Com leftSection, alinha ícone + texto; sem ele, mantém o layout original.
        ...(leftSection
          ? { display: "inline-flex", alignItems: "center", gap: rem(4) }
          : {}),
        ...style,
      }}
      p={BADGE_SIZE_STYLES[size].padding}
      fz={BADGE_SIZE_STYLES[size].fz}
      ff="heading"
      c={color}
      {...others}>
      {leftSection}
      {children}
    </Box>
  );
});

export { Badge };
export default Badge;
